import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { map, Observable, tap } from 'rxjs';

import { ACCESS_ROUTE } from 'app/access/access-routing.constant';
import { AccessService } from 'app/access/services/access.service';
import { AuthenticationDataService } from 'app/core/authentication';
import { TranslationService } from 'app/core/internationalization/translation.service';
import { LanguagesService } from 'app/core/languages/languages.service';
import { LocaleModel } from 'app/core/languages/locale-model';
import { LocalizationService } from 'app/core/localization/localization.service';
import { REGISTER_ROUTE } from 'app/register/register-routing.constant';
import { RegisterService } from 'app/register/services/register.service';
import { STUDENT_ROUTE } from 'app/routing-constants/student-routing.constant';
import { KhAnalyticsService } from 'app/shared/services/kh-analytics.service';

@Component({
  selector: 'kh-login-top-bar',
  styleUrls: ['./login-top-bar.component.less'],
  templateUrl: './login-top-bar.component.html',
  standalone: false
})
export class LoginTopBarComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly localizationService = inject(LocalizationService);
  private readonly analyticsService = inject(KhAnalyticsService);
  private readonly accessService = inject(AccessService);
  private readonly registerService = inject(RegisterService);
  private readonly languagesService = inject(LanguagesService);
  private readonly translationService = inject(TranslationService);
  private readonly authService = inject(AuthenticationDataService);

  public availableLocales$: Observable<LocaleModel[]> | undefined;
  public localeCode: string | undefined;

  public registerRoute = REGISTER_ROUTE.route();
  public loginRoute = ACCESS_ROUTE.route();
  public studentLoginRoute = STUDENT_ROUTE.JOIN_CODE.route();

  @Input() public hideBack = false;
  @Input() public showLogin = false;
  @Input() public showSignup = false;
  @Input() public showStudentLogin = false;
  @Input() public showLanguageSelector = false;

  @Output() public backPressed = new EventEmitter();

  public ngOnInit(): void {
    if (this.showLanguageSelector) {
      this._setAvailableLocales();
    }
  }

  public backButton(): void {
    this.backPressed.emit();
  }

  public setLanguage(localeId: string): void {
    this.analyticsService.trackEventSimple(
      `${this.accessService.role}-Login-Language-Change`,
      undefined,
      {
        NewLanguage: localeId,
        Page: this.router.url
      }
    );
    this.authService.setUserLocale(localeId);
    this.translationService.switchLanguage(localeId);

    this._setAvailableLocales();
  }

  public async navigateStudentLogin(): Promise<void> {
    this.analyticsService.trackEventSimple(
      `${this.accessService.role}-Login-StudentLogin-Click`
    );

    await this.router.navigate(this.studentLoginRoute);
  }

  public async navigateSignUp(): Promise<void> {
    this.analyticsService.trackEventSimple(
      `${this.accessService.role}-Login-SignUp-Click`
    );
    await this.router.navigate(this.registerService.getRegisterRoute(this.accessService.role));
  }

  private _setAvailableLocales(): void {
    this.availableLocales$ = this.languagesService.getAvailableLocales()
      .pipe(
        map((locales) => locales.filter((locale) => locale.LocaleId !== this.translationService.getCurrentLanguage() as string)),
        tap(() => this.localeCode = this.localizationService.getLocaleCode())
      );
  }
}
